import { ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import { rm } from 'utils';
import { useToggle } from 'hooks';
import { Button, LoaderButton } from 'components/Button';
import { ToolTip } from 'components/Shared/ToolTip';
import { isMobile } from 'components/Shared/responsive';
import styles from './discount.scss';
import { Field } from 'redux-form';
import { RenderLabeledInput } from 'components/Shared/Form/inputs';
import { getTotal, getTotalDiscountAmount } from 'views/CheckoutPage/selector';
import { useDidMount } from '@bygghemmait/hooks';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { useUpdateDiscount } from './useUpdateDiscount';

interface DiscountProps {
  disabled: boolean;
}

const DISCOUNT_CODE_MAXLENGTH = 20;

export const Discount = ({ disabled }: DiscountProps) => {
  const mobile = useSelector(isMobile);
  const [isOpen, toggleOpen] = useToggle(false);
  const { errorMessage, submitDiscount, setCode, code, isLoading } = useUpdateDiscount();

  const onDiscountCodeChange = (_: ChangeEvent, v: string) => setCode(v);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    submitDiscount();
  };

  const internalToggleOpen = () => {
    if (disabled) return;
    toggleOpen();
  };

  const error = errorMessage && <div className={styles.error}>{errorMessage}</div>;
  const total = useSelector(getTotal);
  const totalDiscount = useSelector(getTotalDiscountAmount);

  useDidMount(() => {
    if (totalDiscount <= 0) return;
    toggleOpen();
  });

  return (
    <>
      <div className={styles.discount}>
        {!isOpen && total > 0 && (
          <Button
            onClick={internalToggleOpen}
            appearance="link"
            className={styles.discountButton}
            {...getQaDataAttr('pay-with-discount')}
          >
            {rm('checkout_giftcard_button')}
          </Button>
        )}

        {isOpen && (
          <div className={styles.open}>
            <h5 className={styles.title}>
              {rm('checkout_discount_voucher')}
              <ToolTip className={styles.tooltip} text={<Tooltip />} />
            </h5>

            <div className={styles.inputs}>
              <div className={styles.input}>
                <Field
                  name="discountCode"
                  component={RenderLabeledInput}
                  props={{ customLabel: rm('checkout_giftcard_code') }}
                  maxLength={DISCOUNT_CODE_MAXLENGTH}
                  onKeyPress={handleKeyPress}
                  invalid={!!error}
                  value={code}
                  onChange={onDiscountCodeChange}
                />
                {mobile && error}
              </div>

              <LoaderButton
                loading={isLoading}
                onClick={() => submitDiscount()}
                appearance="whitePill"
                {...getQaDataAttr('activate-discount-code')}
              >
                {rm('checkout_giftcard_activate')}
              </LoaderButton>
            </div>
            {!mobile && error}
          </div>
        )}
      </div>
    </>
  );
};

const Tooltip = () => {
  return (
    <span>
      <strong>{rm('checkout_giftcard_help_header')}</strong>
      <p dangerouslySetInnerHTML={{ __html: rm('checkout_giftcard_help_body') }} />
    </span>
  );
};
